import type { MouseEvent } from 'react';
import type { QuestionChoice } from '@/types';
import type { ChoiceValue } from '@/components/control/types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Control from '@/components/control';
import InputControl from '@/components/input-control';
import { useContext } from '@/components/slider-type/context';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { SliderSlice, selectAnswersDefaultByKey } from '@/redux/slider';
import Converter from '@/utils/converter';
import { path } from '@/utils/helper';
import '@/components/slider-type/components/cards/cards.css';

const NO_CARD = 'NO_CARD';
/**
 * TODO: "-no-answer-" is used because slide.choices contains "" as value and it can lead
 * to UI state with 2 selected items, f.e. 1st for isPreselected and 2nd for "" value, "no-answer"
 */
class AdapterV1 {
  private readonly choices: QuestionChoice[];
  private readonly multiple: boolean;
  private readonly noAnswers: '-no-answer-';

  constructor(choices: QuestionChoice[] = [], multiple: boolean = false) {
    this.choices = choices;
    this.multiple = multiple;
    this.noAnswers = '-no-answer-';
  }

  private getChoicesValue(): string[] {
    return this.choices.map((c) => c.value);
  }

  private fromStringToArray(string: string = '') {
    const array = [];
    for (const part of this.getChoicesValue()) {
      if (string.includes(part)) {
        array.push(part);
        string = string.replace(part, '');
      }
    }
    return array;
  }

  private addArray(currentAnswerString: string, valueArray: string[]) {
    return valueArray.reduce(
      (acc, currentAnswer) => this.addString(Converter.Answer.FromArrayToMultiple(acc), currentAnswer),
      this.fromStringToArray(currentAnswerString),
    );
  }

  private addString(currentAnswerString: string, valueString: string) {
    const currentAnswerArray = this.fromStringToArray(currentAnswerString);
    if (this.multiple) {
      return currentAnswerArray.includes(valueString)
        ? currentAnswerArray.filter((c) => c !== valueString)
        : [...currentAnswerArray, valueString];
    } else {
      return [...currentAnswerArray.filter((c) => !this.getChoicesValue().includes(c)), valueString];
    }
  }

  public add(currentAnswerString: string, value: string | string[]) {
    const answer =
      typeof value === 'string'
        ? this.addString(currentAnswerString, value).filter(Boolean)
        : this.addArray(currentAnswerString, value).filter(Boolean);
    if (answer.length) {
      return answer;
    } else {
      return [this.noAnswers];
    }
  }
}

class AdapterV2 {
  private readonly multiple: boolean;
  private readonly noAnswers: '-no-answer-';

  constructor(multiple: boolean = false) {
    this.multiple = multiple;
    this.noAnswers = '-no-answer-';
  }

  public getValue(value: string): ChoiceValue {
    return this.multiple ? Converter.Answer.FromMultipleToArray(value) : value;
  }

  public getNewValue(value: ChoiceValue): string {
    return this.multiple ? Converter.Answer.FromArrayToMultiple(value as string[]) : (value as string);
  }
}

export default function Cards() {
  const { slide, onNext } = useContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const answer = useAppSelector(selectAnswersDefaultByKey(slide.key));
  const currenQuestionKey = useAppSelector(SliderSlice.selectors.currentQuestionKey);
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const switchSlidesMode = useAppSelector(ConfigSlice.selectors.switchSlidesMode);
  const [hoveredCard, setHoveredCard] = useState<string>(NO_CARD);

  const adapterV1 = new AdapterV1(slide.choices, slide.multiple);
  const adapterV2 = new AdapterV2(slide.multiple);

  useEffect(() => {
    if (currenQuestionKey !== slide.key) return;
    const preselectedCards = (slide.choices || []).filter((c) => !!c.isPreselected);
    if (!!preselectedCards.length) {
      const preselectedValues = preselectedCards.map((c) => c.value);
      const newAnswer = adapterV1.add(answer, preselectedValues);
      dispatch(
        SliderSlice.actions.setAnswersDefaultByKey({
          key: slide.key,
          value: Converter.Answer.FromArrayToMultiple(newAnswer),
        }),
      );
    }
  }, [currenQuestionKey]);

  const onCardClickV1 = (cardValue: string, e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.blur();
    const newAnswer = adapterV1.add(answer, cardValue);

    if (switchSlidesMode === 'mixed' || switchSlidesMode === 'on-card') {
      dispatch(
        SliderSlice.actions.setAnswersDefaultByKey({
          key: slide.key,
          value: Converter.Answer.FromArrayToMultiple(newAnswer),
        }),
      );
      if (slide.multiple) return;
      setTimeout(() => onNext('card', Converter.Answer.FromArrayToMultiple(newAnswer)), 0);
    }
    if (switchSlidesMode === 'on-next') {
      dispatch(
        SliderSlice.actions.setAnswersDefaultByKey({
          key: slide.key,
          value: Converter.Answer.FromArrayToMultiple(newAnswer),
        }),
      );
    }
  };

  const onCardClickV2 = (cardValue: string) => {
    switch (switchSlidesMode) {
      case 'mixed':
      case 'on-card':
        dispatch(SliderSlice.actions.setAnswersDefaultByKey({ key: slide.key, value: cardValue }));
        if (slide.multiple) return;
        setTimeout(() => onNext('card', cardValue), 0);
        break;
      case 'on-next':
        dispatch(SliderSlice.actions.setAnswersDefaultByKey({ key: slide.key, value: cardValue }));
        break;
      default:
        throw new Error(`Unsupported slide switch mode ${switchSlidesMode}`);
    }
  };

  const isCardChecked = (answer: string, value: string) => {
    if (answer === '-no-answer-' && value === '') return true;
    if (!answer || value === '') return false;
    return answer.includes(value);
  };

  if (!slide.choices || (slide.type !== 'multiple' && slide.type !== 'multiple-text')) return null;

  if (cssVersion === 1) {
    return (
      <ul
        data-without-icon={slide.type === 'multiple-text'}
        data-control-type={slide.multiple ? 'checkbox' : 'radio'}
        className='slider-type-cards'
      >
        {slide.choices.map(({ value, label, icon }) => (
          <li key={label} className='slider-type-cards-item'>
            <button
              data-checked={isCardChecked(answer, value)}
              type='button'
              className='slider-type-cards-button'
              onClick={(e) => onCardClickV1(value, e)}
              onMouseEnter={() => setHoveredCard(value)}
              onMouseLeave={() => setHoveredCard(NO_CARD)}
            >
              <div className='slider-type-cards-radio'>
                <InputControl
                  shape={slide.multiple ? 'checkbox' : 'radio'}
                  checked={isCardChecked(answer, value)}
                  hovered={hoveredCard === value}
                  size={slide.multiple ? 'lg' : 'xl'}
                />
              </div>
              <div data-image={icon} className='slider-type-cards-image' />
              <span className='slider-type-cards-label' dangerouslySetInnerHTML={{ __html: t(label) }} />
            </button>
          </li>
        ))}
      </ul>
    );
  } else {
    return slide.type === 'multiple' ? (
      <Control.CardImage
        sprite={path('/images/sprite.svg')}
        spriteMobile={path('/images/sprite_mobile.svg')}
        id={slide.key}
        choices={slide.choices}
        value={adapterV2.getValue(answer)}
        onChange={(value) => onCardClickV2(adapterV2.getNewValue(value))}
      />
    ) : (
      <Control.CardText
        id={slide.key}
        choices={slide.choices}
        value={adapterV2.getValue(answer)}
        onChange={(value) => onCardClickV2(adapterV2.getNewValue(value))}
      />
    );
  }
}
